import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import toast from 'react-hot-toast';
import { BundlesModel } from '../model/subscriptionBundles.model';
import { usePushNotificationModel } from '../model/pushNotification.model';
import { z } from 'zod';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getPlatformBundleModalCta,
  getPnDefaultMessage,
} from './editPlatformBundle.vm.mapper';
import { format } from 'date-fns';
import { updateHash } from '../../common/helpers';
import { SubscriptionBundle } from '../../common/bundle.types';
import { BundlePricing } from 'generated/graphql';
import { trackEvent } from '@services/mixpanel';

type PayloadMap = {
  editBundle: SubscriptionBundle;
  cancelEditBundle: null;
  openPnModal: null;
  closePnModal: null;
  savePN: null;
  copyUpgradeLink: string;
};

export type EditPlatformBundleAction = Actions<PayloadMap>;
export type EditPlatformBundleDispatcher = FSpread<PayloadMap>;

export type EditPlatformBundleVM = {
  editPlatformBundle: (b: SubscriptionBundle) => void;
  platformBundleModal: {
    isOpen: boolean;
    close: () => void;
    message: string;
    sentOn?: string;
    cta: Array<{
      onClick: () => void;
      label: string;
      disabled?: boolean;
    }>;
    count: number;
  };
  pushNotificationModal: {
    title: string;
    isOpen: boolean;
    close: () => void;
    save: () => void;
    submitting: boolean;
    control: Control<PnFormValues, any, PnFormValues>;
  };
};

export const pushNotificationSchema = z.object({
  message: z.string().trim().min(1, { message: 'Message text is required' }),
});

export const formDefaultValues = {
  message: undefined,
};

export type PnFormValues = z.infer<typeof pushNotificationSchema>;

export const useEditPlatformBundleVM = ({
  model,
}: {
  model: Async<BundlesModel>;
}): EditPlatformBundleVM => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [bundleId, setBundleId] = useState<number | undefined>(undefined);
  const [pnModalOpen, setPnModalOpen] = useState<boolean>(false);
  const { sendNotification } = usePushNotificationModel();

  const { handleSubmit, control, reset } = useForm<PnFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(pushNotificationSchema),
  });

  useEffect(() => {
    if (!bundleId) {
      reset(
        {
          message: undefined,
        },
        { keepDefaultValues: false },
      );

      return;
    }

    const bundle =
      model.type === 'fetched'
        ? model.value.initialSubscriptionBundles.find(
            (b) =>
              b.id === bundleId && b.pricing === BundlePricing.Subscription,
          )
        : undefined;

    const creatorName =
      model.type === 'fetched' ? model.value.creatorName : undefined;
    const message =
      bundle?.pushNotificationMessage || getPnDefaultMessage(creatorName);

    reset(
      {
        message,
      },
      { keepDefaultValues: false },
    );
  }, [bundleId]);

  const modelValue = model.type === 'fetched' ? model.value : null;

  const editBundle = (bundle: SubscriptionBundle) => {
    setBundleId(bundle.id);
    updateHash(`#${bundle.id}`);
  };

  const editedBundle = modelValue?.initialSubscriptionBundles
    .filter((b) => b.isPlatformBundle)
    .find((b) => b.id === bundleId);

  const dispatch: EditPlatformBundleDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditPlatformBundleAction;
    switch (action.type) {
      case 'editBundle':
        editBundle(action.payload);
        return null;
      case 'cancelEditBundle':
        setBundleId(undefined);
        updateHash(window.location.pathname);
        return null;
      case 'openPnModal':
        trackEvent({
          type: 'Exclusive Sub Push Created',
          payload: {
            count: (editedBundle?.pushNotificationUsageCount || 0) + 1,
          },
        });
        setPnModalOpen(true);
        return null;
      case 'closePnModal':
        setPnModalOpen(false);
        return null;
      case 'savePN':
        setSubmitting(true);
        sendPN();
        return null;
      case 'copyUpgradeLink':
        trackEvent({
          type: 'Exclusive Sub Upgrade Link Copied',
        });
        copyUpgradeLink(action.payload);
        return null;
    }
  };

  const copyUpgradeLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast.success('Link was copied to clipboard');
  };

  const sendPN = handleSubmit(
    (values) => {
      if (!editedBundle?.id) {
        toast.error('Could not send push notification. Please refresh page.');
        return;
      }

      sendNotification({
        bundleId: editedBundle.id,
        message: values.message,
      })
        .then(() => {
          trackEvent({
            type: 'Exclusive Sub Push Sent',
            payload: {
              count: editedBundle.pushNotificationUsageCount + 1,
            },
          });
          toast.success('Push notification has been sent successfully');
          modelValue?.refetch();
          setPnModalOpen(false);
        })
        .catch((error) => {
          toast.error(error);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    () => {
      setSubmitting(false);
    },
  );

  return {
    editPlatformBundle: (b) => dispatch('editBundle', b),
    platformBundleModal: {
      isOpen: !!bundleId,
      close: () => dispatch('cancelEditBundle'),
      message: editedBundle?.pushNotificationMessage || '',
      sentOn: editedBundle?.pushNotificationSentAt
        ? format(
            new Date(editedBundle?.pushNotificationSentAt),
            'dd.MM.yyyy, h:mm a',
          )
        : undefined,
      cta: getPlatformBundleModalCta({ editedBundle, dispatch }),
      count: editedBundle?.pushNotificationUsageCount || 0,
    },
    pushNotificationModal: {
      title:
        editedBundle?.pushNotificationUsageCount &&
        editedBundle?.pushNotificationUsageCount > 0
          ? 'Edit Push Notification'
          : 'Create Push Notification',
      isOpen: pnModalOpen,
      close: () => dispatch('closePnModal'),
      save: () => dispatch('savePN'),
      submitting: isSubmitting,
      control,
    },
  };
};
